import React from 'react';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import { useTranslate } from 'src/locales';
import { useBaseQuery } from 'src/api/core/useBaseQuery';
import dayjs from 'dayjs';

interface MedicineDetailProps {
  onClose: () => void;
  patientId?: string | number;
  srumId?: number;
}

const MedicineDetail: React.FC<MedicineDetailProps> = ({ onClose, patientId, srumId }) => {
  const { t } = useTranslate();

  // API isteği - useBaseQuery doğrudan response.data döndürür
  const { data: apiData, isLoading } = useBaseQuery<any>({
    endpoint: (patientId && srumId)
      ? `/nurse/api/Order/GetPatientOrderDetail?patientId=${patientId}&srumId=${srumId}`
      : '',
    queryKey: ['patient-order-detail', patientId, srumId],
    options: {
      enabled: !!patientId && !!srumId,
      staleTime: 0,
    },
  });

  // API dizi döndürüyor, ilk elemanı al
  const detailData = Array.isArray(apiData) ? apiData[0] : apiData;

  // Tag'ler - API field'larına göre boolean kontrol
  const tags = [
    { label: t('nurse.order-medicine.detail.tags.yri'), active: detailData?.ishighrisk === '1' },
    { label: t('nurse.order-medicine.detail.tags.hyi'), active: detailData?.ispatientdrug === '1' },
    { label: t('nurse.order-medicine.detail.tags.ei'), active: false },
    { label: t('nurse.order-medicine.detail.tags.lh'), active: detailData?.dontrequestfrompha === '1' },
    { label: t('nurse.order-medicine.detail.tags.hdk'), active: detailData?.usepatientwh === '1' },
    { label: t('nurse.order-medicine.detail.tags.tpn'), active: detailData?.totalparenteralnutrition === '1' },
    { label: t('nurse.order-medicine.detail.tags.mixture'), active: detailData?.ismixture === '1' },
    { label: t('nurse.order-medicine.detail.tags.tad'), active: detailData?.istherapeuticspaceft === '1' },
    { label: t('nurse.order-medicine.detail.tags.narcotic'), active: detailData?.isnarcotic === '1' },
    { label: t('nurse.order-medicine.detail.tags.urgent'), active: detailData?.isurgent === '1' },
  ];

  // Genel bilgiler alanları: 1. satır
  const firstRowFields: { label: string; value: string }[] = [
    { label: t('nurse.order-medicine.detail.is-mixture'), value: detailData?.ismixture === '1' ? 'Evet' : 'Hayır' },
    { label: t('nurse.order-medicine.detail.plan-time'), value: detailData?.plandate ? dayjs(detailData.plandate).format('HH:mm') : '-' },
    { label: t('nurse.order-medicine.card-fields.pharmaceutical'), value: detailData?.drugformtypename || '-' },
    { label: t('nurse.order-medicine.card-fields.dose'), value: detailData?.dose || '-' },
    { label: t('nurse.order-medicine.card-fields.application-time'), value: detailData?.appdate ? dayjs(detailData.appdate).format('HH:mm') : '-' },
    { label: t('nurse.order-medicine.card-fields.application-route'), value: detailData?.dumname || '-' },
    { label: t('nurse.order-medicine.card-fields.application-duration'), value: detailData?.druginfusionspeed ? `${detailData.druginfusionspeed} ${detailData.druginfusionunit || ''}`.trim() : '-' },
    { label: t('nurse.order-medicine.detail.document-no'), value: detailData?.documentnumber || '-' },
    { label: t('nurse.order-medicine.card-fields.unit'), value: detailData?.dosetypename || '-' },
    { label: 'AG', value: detailData?.sfarcode || '-' },
    { label: t('nurse.order-medicine.detail.purpose'), value: detailData?.usepurposes || '-' },
    { label: t('nurse.order-medicine.detail.feature'), value: detailData?.features || '-' },
    { label: t('nurse.order-medicine.detail.risk-category'), value: detailData?.riskcategory || '-' },
    { label: t('nurse.order-medicine.detail.risk-name'), value: detailData?.riskname || '-' },
  ];

  // Genel bilgiler alanları: 2. satır
  const secondRowFields: { label: string; value: string }[] = [
    { label: t('nurse.order-medicine.card-fields.unit'), value: detailData?.dosetypename || '-' },
    { label: t('nurse.order-medicine.detail.infusion-rate'), value: detailData?.druginfusionspeed ? `${detailData.druginfusionspeed}` : '-' },
    { label: t('nurse.order-medicine.detail.infusion-unit'), value: detailData?.druginfusionunit || '-' },
    { label: t('nurse.order-medicine.detail.application-form'), value: detailData?.useasnecessary === '1' ? 'Gerektiğinde' : '-' },
    { label: t('nurse.order-medicine.detail.plan-time-short'), value: detailData?.plandate ? dayjs(detailData.plandate).format('HH:mm') : '-' },
    { label: t('nurse.order-medicine.detail.plan-date'), value: detailData?.plandate ? dayjs(detailData.plandate).format('DD.MM.YYYY') : '-' },
    { label: t('nurse.order-medicine.detail.status'), value: detailData?.statusname || '-' },
    { label: t('nurse.order-medicine.detail.return-status'), value: detailData?.srumresult?.trim() || '-' },
    { label: t('nurse.order-medicine.detail.requested-drug'), value: detailData?.reqmatname || '-' },
    { label: t('nurse.order-medicine.detail.fulfilled-drug'), value: detailData?.matname || '-' },
    { label: t('nurse.order-medicine.detail.application-form-full'), value: detailData?.ordtypename || '-' },
    { label: t('nurse.order-medicine.detail.result'), value: detailData?.srumresult?.trim() || '-' },
    { label: t('nurse.order-medicine.detail.order-type'), value: detailData?.ordtypename || '-' },
  ];

  // Notlar alanları: 1. satır
  const firstRowNotes: { label: string; value: string }[] = [
    { label: t('nurse.order-medicine.detail.drug-note'), value: detailData?.stocknote?.trim() || '-' },
    { label: t('nurse.order-medicine.detail.overdose-note'), value: detailData?.maxdosenote?.trim() || '-' },
    { label: t('nurse.order-medicine.detail.process-note'), value: detailData?.cnote?.trim() || '-' },
    { label: t('nurse.order-medicine.detail.request-note'), value: detailData?.srnote?.trim() || '-' },
  ];

  // Notlar alanları: 2. satır
  const secondRowNotes: { label: string; value: string }[] = [
    { label: t('nurse.order-medicine.detail.pharmacy-note'), value: detailData?.appnote?.trim() || '-' },
    { label: t('nurse.order-medicine.detail.application-note'), value: detailData?.cnote?.trim() || '-' },
    { label: t('nurse.order-medicine.detail.result'), value: detailData?.srumresult?.trim() || '-' },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1800,
        bgcolor: '#fff',
        borderRadius: 2.5,
        boxShadow: '0px 4px 25px rgba(0,0,0,0.25)',
        p: 2.5,
      }}
    >
      {/* İlaç Adı & SRUMMatNames */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, fontSize: 16, mb: 0.5 }}
        >
          {detailData?.reqmatname || detailData?.matname || '-'}
        </Typography>
        {detailData?.srummatnames && detailData.srummatnames !== detailData?.reqmatname && (
          <Typography sx={{ fontSize: 13, color: '#7B849E' }}>
            {detailData.srummatnames}
          </Typography>
        )}
      </Box>

      {/* Genel Bilgiler */}
      <Box
        sx={{
          mb: 2,
          borderRadius: 2.5,
          boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
          border: '1px solid #E0E0E0',
          p: 2,
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            borderBottom: '1px solid #E0E0E0',
            pb: 1,
            mb: 1.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: '-0.01em',
            }}
          >
            {t('nurse.order-medicine.detail.general-info')}
          </Typography>
        </Box>

        {/* YRİ / HYİ / ... satırı */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
            fontSize: 12,
            color: '#7B849E',
          }}
        >
          {tags.map((tag, index) => (
            <Stack
              key={`${tag.label}-${index}`}
              spacing={0.5}
              alignItems="center"
              justifyContent="center"
            >
              <Typography
                sx={{
                  fontSize: 12,
                  letterSpacing: '-0.02em',
                  lineHeight: '16px',
                }}
              >
                {tag.label}
              </Typography>
              <Box
                sx={{
                  width: 25,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: tag.active ? 'none' : '1px solid rgba(0,0,0,0.38)',
                    bgcolor: tag.active ? '#2979C3' : 'transparent',
                  }}
                />
              </Box>
            </Stack>
          ))}
        </Box>

        {/* Detay alanları: 1. Satır: 14 alan */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
              md: 'repeat(7, minmax(0, 1fr))',
              lg: 'repeat(14, minmax(0, 1fr))',
            },
            columnGap: 2,
            rowGap: 2,
            mb: 2,
          }}
        >
          {firstRowFields.map((field, idx) => (
            <Box key={`first-${idx}-${field.label}`}>
              <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
                {field.label}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                {field.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 2. Satır: 13 alan */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
              md: 'repeat(7, minmax(0, 1fr))',
              lg: 'repeat(14, minmax(0, 1fr))',
            },
            columnGap: 2,
            rowGap: 2,
          }}
        >
          {secondRowFields.map((field, idx) => (
            <Box key={`second-${idx}-${field.label}`}>
              <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
                {field.label}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                {field.value}
              </Typography>
            </Box>
          ))}
          {/* Boş alan - 14. kolon için */}
          <Box sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' } }} />
        </Box>
      </Box>

      {/* Notlar */}
      <Box
        sx={{
          mb: 2,
          borderRadius: 2.5,
          boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
          border: '1px solid #E0E0E0',
          p: 2,
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            borderBottom: '1px solid #E0E0E0',
            pb: 1,
            mb: 1.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: '-0.01em',
            }}
          >
            {t('nurse.order-medicine.detail.notes')}
          </Typography>
        </Box>

        {/* 1. Satır: 4 alan */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
              lg: 'repeat(7, minmax(0, 1fr))',
            },
            columnGap: 2,
            rowGap: 2,
            mb: 2,
          }}
        >
          {firstRowNotes.map((field, idx) => (
            <Box
              key={`note1-${idx}-${field.label}`}
              sx={{
                gridColumn: {
                  lg: 'span 1',
                },
              }}
            >
              <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
                {field.label}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                {field.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 2. Satır: 3 alan */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(7, minmax(0, 1fr))',
            },
            columnGap: 2,
            rowGap: 2,
          }}
        >
          {secondRowNotes.map((field, idx) => (
            <Box
              key={`note2-${idx}-${field.label}`}
              sx={{
                gridColumn: {
                  lg: 'span 1',
                },
              }}
            >
              <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
                {field.label}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                {field.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Süre / Onay */}
      <Box
        sx={{
          borderRadius: 2,
          boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
          border: '1px solid #E0E0E0',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1.5,
            pb: 1,
            borderBottom: '1px solid #E0E0E0',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
            }}
          >
            {t('nurse.order-medicine.detail.duration-approval')}
          </Typography>
        </Box>

        {/* 1. Satır: 7 alan */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
              md: 'repeat(7, minmax(0, 1fr))',
            },
            columnGap: 2,
            rowGap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.verification-time')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.authenticationtime ? dayjs(detailData.authenticationtime).format('DD.MM.YYYY HH:mm:ss') : '-'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.verification-user')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.authenticator || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.processor')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.srumcuser || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.process-time')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.srumctime ? dayjs(detailData.srumctime).format('DD.MM.YYYY HH:mm:ss') : '-'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.previous-app-time-short')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.appdatepredate ? dayjs(detailData.appdatepredate).format('DD.MM.YYYY HH:mm') : '-'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.previous-app-time-change-user-short')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.appdateprecuser || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.previous-app-time-change-time-short')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.appdateprectime ? dayjs(detailData.appdateprectime).format('DD.MM.YYYY HH:mm') : '-'}
            </Typography>
          </Box>
        </Box>

        {/* 2. Satır: 4 alan */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            columnGap: 2,
            rowGap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.order-time-short')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.srptime ? dayjs(detailData.srptime).format('DD.MM.YYYY HH:mm') : '-'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.previous-plan-time')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.plandatepredate ? dayjs(detailData.plandatepredate).format('DD.MM.YYYY HH:mm') : '-'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.previous-plan-time-change-user')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.plandatepreuser || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#7B849E' }}>
              {t('nurse.order-medicine.detail.previous-plan-time-change-time')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {detailData?.plandateprectime ? dayjs(detailData.plandateprectime).format('DD.MM.YYYY HH:mm') : '-'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          width: '100%',
          height: '56px',
          boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
          borderRadius: '20px',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          px: 1.625,
          gap: 1.125,
          flexWrap: 'wrap',
          mt: 2,
        }}
      >
        {/* Y.R.İlaç */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
          <Box
            sx={{
              height: '22px',
              width: '22px',
              borderRadius: '4px',
              backgroundColor: '#fff',
              border: '1px solid #000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pr: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 11, lineHeight: '22px', textTransform: 'capitalize' }}>
              {t('nurse.order-medicine.detail.legend.yr-drug')}
            </Typography>
          </Box>
        </Box>

        {/* N.İlaç */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
          <Box
            sx={{
              height: '22px',
              width: '22px',
              borderRadius: '4px',
              backgroundColor: '#800000',
              border: '1px solid #000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pr: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 11, lineHeight: '22px', textTransform: 'capitalize' }}>
              {t('nurse.order-medicine.detail.legend.n-drug')}
            </Typography>
          </Box>
        </Box>

        {/* Min. Değer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
          <Box
            sx={{
              height: '22px',
              width: '22px',
              borderRadius: '4px',
              backgroundColor: '#cab468',
              border: '1px solid #000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pr: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 11, lineHeight: '22px', textTransform: 'capitalize' }}>
              {t('nurse.order-medicine.detail.legend.min-value')}
            </Typography>
          </Box>
        </Box>

        {/* Max Değer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
          <Box
            sx={{
              height: '22px',
              width: '22px',
              borderRadius: '4px',
              backgroundColor: '#977dac',
              border: '1px solid #000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pr: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 11, lineHeight: '22px', textTransform: 'capitalize' }}>
              {t('nurse.order-medicine.detail.legend.max-value')}
            </Typography>
          </Box>
        </Box>

        {/* Kritik Değer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
          <Box
            sx={{
              height: '22px',
              width: '22px',
              borderRadius: '4px',
              backgroundColor: '#df8200',
              border: '1px solid #000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pr: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 11, lineHeight: '22px', textTransform: 'capitalize' }}>
              {t('nurse.order-medicine.detail.legend.critical-value')}
            </Typography>
          </Box>
        </Box>

        {/* T. Aralığı Dar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
          <Box
            sx={{
              height: '22px',
              width: '22px',
              borderRadius: '4px',
              backgroundColor: '#800080',
              border: '1px solid #000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pr: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 11, lineHeight: '22px', textTransform: 'capitalize' }}>
              {t('nurse.order-medicine.detail.legend.narrow-range')}
            </Typography>
          </Box>
        </Box>

        {/* P. İlaç */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
          <Box
            sx={{
              height: '22px',
              width: '22px',
              borderRadius: '4px',
              backgroundColor: '#008000',
              border: '1px solid #000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pr: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 11, lineHeight: '22px', textTransform: 'capitalize' }}>
              {t('nurse.order-medicine.detail.legend.p-drug')}
            </Typography>
          </Box>
        </Box>

        {/* s. İlaç */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
          <Box
            sx={{
              height: '22px',
              width: '22px',
              borderRadius: '4px',
              backgroundColor: '#ff80ff',
              border: '1px solid #000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pr: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 11, lineHeight: '22px', textTransform: 'capitalize' }}>
              {t('nurse.order-medicine.detail.legend.s-drug')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MedicineDetail;
